/**
 * Utility functions for consistent note formatting across the application
 */

/**
 * Format date as DD/MM/YY
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateShort = (date) => {
  if (!date) return new Date().toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: '2-digit' });
  return new Date(date).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

/**
 * Get source context based on entity type and note type
 * @param {string} entityType - Type of entity (enquiry, offer, order)
 * @param {string} noteType - Type of note (general, design, customer)
 * @returns {string} Source context string
 */
export const getSourceContext = (entityType, noteType = 'general') => {
  if (noteType === 'design') {
    switch (entityType) {
      case 'enquiry': return 'Enquiry Design Notes';
      case 'offer': return 'Offer Design Notes';
      case 'order': return 'Order Design Notes';
      default: return 'Design Notes';
    }
  } else if (noteType === 'customer') {
    switch (entityType) {
      case 'enquiry': return 'Customer Notes';
      case 'offer': return 'Offer Customer Notes';
      case 'order': return 'Order Customer Notes';
      default: return 'Customer Notes';
    }
  } else {
    switch (entityType) {
      case 'enquiry': return 'Enquiries';
      case 'offer': return 'Offers';
      case 'order': return 'Orders';
      case 'organization': return 'Organisation Notes';
      case 'stationeryOrder': return 'Stationery Orders';
      case 'artwork': return 'Artwork Upload';
      default: return 'System';
    }
  }
};

/**
 * Create a structured note entry with timestamp, user, source, and content
 * @param {string} content - Note content
 * @param {string} userName - User who created the note
 * @param {string} entityType - Type of entity (enquiry, offer, order)
 * @param {string} noteType - Type of note (general, design, customer)
 * @param {string|Date} date - Date of the note (optional, defaults to current date)
 * @returns {string} Formatted note string
 */
export const createStructuredNote = (content, userName, entityType, noteType = 'general', date = null) => {
  const noteDate = formatDateShort(date);
  const sourceContext = getSourceContext(entityType, noteType);
  return `(${noteDate}) ${userName} from ${sourceContext}: ${content}`;
};

/**
 * Create a structured note entry for offer creation
 * @param {string} content - Note content
 * @param {string} userName - User who created the note
 * @param {string} noteType - Type of note (general for notes, design for designNotes)
 * @returns {string} Formatted note string
 */
export const createOfferCreationNote = (content, userName, noteType = 'general') => {
  const noteDate = formatDateShort();
  const sourceContext = noteType === 'design' ? 'Offer Creation' : 'Offer Creation';
  return `(${noteDate}) ${userName} from ${sourceContext}: ${content}`;
};

/**
 * Decode HTML entities that may have been left over from older (buggy) sanitization
 * so legacy note content displays correctly instead of showing raw entities like
 * "05&#x2F;08&#x2F;26".
 * @param {string} str
 * @returns {string}
 */
export const decodeHtmlEntities = (str) => {
  if (!str || typeof str !== 'string') return str;
  return str
    .replace(/&#x2F;/gi, '/')
    .replace(/&#x27;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&amp;/gi, '&');
};

// Map raw "source context" strings (as produced by getSourceContext) to a short,
// human-friendly label shown as a prefix on note cards, e.g. "(Created from Ad Orders)"
const SOURCE_LABEL_MAP = {
  'Offer Creation': 'Offer Creation',
  'Offers': 'Offer Creation',
  'Offer Design Notes': 'Design Note',
  'Offer Customer Notes': 'Customer Note',
  'Orders': 'Order Creation',
  'Order Creation': 'Order Creation',
  'Order Design Notes': 'Design Note',
  'Order Customer Notes': 'Customer Note',
  'Enquiries': 'Enquiry',
  'Enquiry Design Notes': 'Design Note',
  'Follow-up Notes': 'Follow-up',
  'Customer Notes': 'Customer Note',
  'Sales Notes': null,
  'System': null,
  'Organisation Notes': null,
  'Stationery Orders': 'Stationery Order',
  'Artwork Upload': 'Artwork Upload'
};

/**
 * Get a short, friendly source label for display, e.g. "Created from Ad Orders"
 * @param {string} sourceContext - Raw source context string
 * @returns {string|null}
 */
export const getFriendlySourceLabel = (sourceContext) => {
  if (!sourceContext) return null;
  const trimmed = sourceContext.trim();
  if (trimmed in SOURCE_LABEL_MAP) return SOURCE_LABEL_MAP[trimmed];
  return `Created from ${trimmed}`;
};

// Parses a date formatted as DD/MM/YY (as produced by formatDateShort) into a Date
const parseShortDate = (raw) => {
  const match = raw && raw.match(/^(\d{2})\/(\d{2})\/(\d{2})$/);
  if (!match) return null;
  const [, d, m, y] = match;
  return new Date(2000 + parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
};

// Parses a timestamp formatted like "5/08/2026, 1:54:23 pm" (en-AU toLocaleString)
// which is how notes were previously stamped inline within AdvertisingOrderForm.
const parseLegacyTimestamp = (raw) => {
  if (!raw) return null;
  const match = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4}),?\s*(\d{1,2}):(\d{2}):(\d{2})\s*(am|pm)/i);
  if (!match) return null;
  const [, d, m, y, hh, mm, ss, ap] = match;
  let hour = parseInt(hh, 10);
  if (/pm/i.test(ap) && hour < 12) hour += 12;
  if (/am/i.test(ap) && hour === 12) hour = 0;
  return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10), hour, parseInt(mm, 10), parseInt(ss, 10));
};

/**
 * Parse a (possibly multi-note) blob of text - as stored in fields like
 * order.notes / order.designNotes / offer.notes - into an array of clean,
 * individually-attributed note objects for consistent display.
 *
 * Handles the formats used across the app:
 *  - "(DD/MM/YY) User Name from Source Context: content"  (current structured format)
 *  - "[timestamp]\ncontent"                                (legacy in-form notes)
 *  - plain text with no structure                          (e.g. system notes)
 *
 * @param {string} blob - Raw note text, possibly containing multiple notes joined by blank lines
 * @param {Object} fallback - Fallback metadata used when a segment has no structure
 * @param {string} [fallback.createdBy] - Fallback author name
 * @param {string|Date} [fallback.createdAt] - Fallback date
 * @param {string} [fallback.sourceLabel] - Fallback source label
 * @param {string} [fallback.noteType] - Note type (general/design/customer)
 * @param {string} [fallback.idPrefix] - Prefix used to build stable-ish ids
 * @returns {Array<{id: string, content: string, createdBy: string, createdAt: string, sourceLabel: string|null, noteType: string, isNew: boolean}>}
 */
export const parseNotesBlob = (blob, fallback = {}) => {
  if (!blob || typeof blob !== 'string') return [];

  const idPrefix = fallback.idPrefix || 'note';
  const fallbackDate = fallback.createdAt ? new Date(fallback.createdAt) : new Date();

  const segments = blob.split(/\n{2,}/).map(s => s.trim()).filter(Boolean);

  return segments.map((segment, index) => {
    const decoded = decodeHtmlEntities(segment);

    // Pattern: "(DD/MM/YY) User Name from Source Context: content"
    const structuredMatch = decoded.match(/^\((\d{2}\/\d{2}\/\d{2})\)\s+(.+?)\s+from\s+([^:]+):\s*([\s\S]*)$/);
    if (structuredMatch) {
      const [, dateStr, userName, sourceContext, content] = structuredMatch;
      const parsedDate = parseShortDate(dateStr);
      return {
        id: `${idPrefix}_${index}`,
        content: content.trim(),
        createdBy: userName.trim(),
        createdAt: (parsedDate || fallbackDate).toISOString(),
        sourceLabel: getFriendlySourceLabel(sourceContext.trim()),
        noteType: fallback.noteType || 'general',
        isNew: false,
        raw: decoded
      };
    }

    // Pattern: "[timestamp]\ncontent" (legacy notes typed directly on the order form)
    const bracketMatch = decoded.match(/^\[(.+?)\]\s*\n?([\s\S]*)$/);
    if (bracketMatch) {
      const [, timestampStr, content] = bracketMatch;
      const parsedDate = parseLegacyTimestamp(timestampStr);
      return {
        id: `${idPrefix}_${index}`,
        content: content.trim(),
        createdBy: fallback.createdBy || 'User',
        createdAt: (parsedDate || fallbackDate).toISOString(),
        sourceLabel: fallback.sourceLabel || null,
        noteType: fallback.noteType || 'general',
        isNew: false,
        raw: decoded
      };
    }

    // Fallback: plain, unstructured text (e.g. "Order created from offer #6")
    return {
      id: `${idPrefix}_${index}`,
      content: decoded,
      createdBy: fallback.createdBy || 'System',
      createdAt: fallbackDate.toISOString(),
      sourceLabel: fallback.sourceLabel || null,
      noteType: fallback.noteType || 'general',
      isNew: false,
      raw: decoded
    };
  });
};

/**
 * Collect all notes from an enquiry with structured format, categorized by type
 * @param {Object} enquiry - Enquiry object
 * @returns {Object} Object with salesNotes, designNotes, and customerNotes arrays
 */
export const collectCategorizedNotesFromEnquiry = (enquiry) => {
  const categorizedNotes = {
    salesNotes: [],
    designNotes: [],
    customerNotes: []
  };
  
  // Collect database notes with structured format
  if (enquiry.salesNotes) {
    const noteDate = formatDateShort(enquiry.createdAt);
    const noteUser = enquiry.salesConsultant || 'Unknown User';
    categorizedNotes.salesNotes.push(`(${noteDate}) ${noteUser} from Sales Notes: ${enquiry.salesNotes}`);
  }
  if (enquiry.customerNotes) {
    const noteDate = formatDateShort(enquiry.createdAt);
    const noteUser = enquiry.salesConsultant || 'Unknown User';
    categorizedNotes.customerNotes.push(`(${noteDate}) ${noteUser} from Customer Notes: ${enquiry.customerNotes}`);
  }
  if (enquiry.followUpNotes) {
    const noteDate = formatDateShort(enquiry.followUpDate || enquiry.createdAt);
    const noteUser = enquiry.salesConsultant || 'Unknown User';
    categorizedNotes.salesNotes.push(`(${noteDate}) ${noteUser} from Follow-up Notes: ${enquiry.followUpNotes}`);
  }
  
  // Collect localStorage notes with structured format, categorized by note type
  const storageKey = `notes_enquiry_${enquiry.id}`;
  const storageNotes = JSON.parse(localStorage.getItem(storageKey) || '[]');
  
  storageNotes.forEach(note => {
    const noteDate = formatDateShort(note.createdAt);
    const noteUser = note.createdBy || 'User';
    
    // Parse note content to determine category based on source context
    const noteContent = note.content || '';
    if (noteContent.includes('from Enquiry Design Notes') || noteContent.includes('from Design Notes')) {
      categorizedNotes.designNotes.push(noteContent);
    } else if (noteContent.includes('from Customer Notes')) {
      categorizedNotes.customerNotes.push(noteContent);
    } else {
      // Default to sales notes for general enquiry notes
      const noteText = `(${noteDate}) ${noteUser} from Enquiries: ${noteContent}`;
      categorizedNotes.salesNotes.push(noteText);
    }
  });
  
  return categorizedNotes;
};

/**
 * Collect all notes from an enquiry with structured format (legacy function for backward compatibility)
 * @param {Object} enquiry - Enquiry object
 * @returns {string} Combined notes string
 */
export const collectAllNotesFromEnquiry = (enquiry) => {
  const categorized = collectCategorizedNotesFromEnquiry(enquiry);
  const allNotes = [
    ...categorized.salesNotes,
    ...categorized.designNotes,
    ...categorized.customerNotes
  ];
  return allNotes.join('\n\n');
};
